import type {
  CapabilitySearchRequest,
  MatchTier,
  ScoreBasis,
  SearchHit,
  SearchResponse,
  TopicTreeCapability,
} from "@models/search";
import { postJson } from "./apiClient";

export { setSearchAccessTokenFn } from "./apiClient";

/** camelCase filter -> the service's snake_case filter, empty -> none. */
function filterToWire(
  filter: CapabilitySearchRequest["filter"],
): Record<string, number> | undefined {
  if (!filter) return undefined;
  const wire = Object.fromEntries(
    Object.entries(filter)
      .filter(([, value]) => typeof value === "number")
      .map(([key, value]) => [key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`), value]),
  );
  return Object.keys(wire).length > 0 ? wire : undefined;
}

function requestToWire(request: CapabilitySearchRequest): Record<string, unknown> {
  return {
    query: request.query,
    top_k: request.topK,
    filter: filterToWire(request.filter),
  };
}

function hitFromWire(hit: Record<string, unknown>): SearchHit {
  return {
    id: hit.id as string,
    similarity: (hit.similarity as number | null) ?? null,
    rawSimilarity: (hit.raw_similarity as number | null) ?? null,
    scoreBasis: (hit.score_basis as ScoreBasis) ?? undefined,
    indexId: hit.index_id as string,
    title: hit.title as string,
    snippet: (hit.snippet as string) ?? undefined,
    thumbnail: (hit.thumbnail as string) ?? undefined,
    reason: (hit.reason as string) ?? undefined,
    sources: (hit.sources as string[]) ?? undefined,
    metadata: (hit.metadata as Record<string, unknown>) ?? undefined,
  };
}

class SearchApi {
  /** Search one topic-tree capability: technical manual or wiring diagram. */
  async search(
    capability: TopicTreeCapability,
    request: CapabilitySearchRequest,
  ): Promise<SearchResponse> {
    const data = await postJson<{
      hits?: Record<string, unknown>[];
      match_tier?: MatchTier;
      degraded?: string[] | null;
    }>(`/${capability}/search`, requestToWire(request));
    return {
      hits: (data.hits ?? []).map(hitFromWire),
      matchTier: data.match_tier,
      degraded: data.degraded ?? [],
    };
  }
}

export const searchApi = new SearchApi();
